import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { CostService } from 'src/app/services/cost.service';
import { CostUnitOfMeasureModel } from 'src/app/models/season-models/cost-unit-of-measure-model';
import { SeasonSharedDataService } from 'src/app/services/season-shared-data.service';
import { SeasonManagerService } from 'src/app/services/season-manager.service';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';

@Component({
    selector: 'app-season-costing',
    templateUrl: './season-costing.component.html',
    styleUrls: ['./season-costing.component.css'],
    standalone: false
})
export class SeasonCostingComponent implements OnInit {

  unitsOfMeasure: CostUnitOfMeasureModel[] = [];
  fixedCostValue: number = 0;
  costInputs: any[] = [];
  isLoading: boolean = true;
  seasonId: number = 0;
  allFieldDetailIds: number[] = [];

  get vrtInputs(): any[] {
    return this.costInputs.filter(i => i.sourceType === 'VRT');
  }

  get asAppliedInputs(): any[] {
    return this.costInputs.filter(i => i.sourceType === 'ASAPPLIED');
  }

  constructor(
    private costService: CostService,
    private seasonSharedDataService: SeasonSharedDataService,
    private seasonManagerService: SeasonManagerService,
    private snackBar: MatSnackBar,
    public nav: NavBarComponent,
  ) {}

  ngOnInit(): void {
    this.seasonSharedDataService.seasonIdData$.subscribe(id => {
      if (id && id > 0) {
        this.seasonId = id;
        this.loadSeasonFields();
      }
    });
    this.loadUnitsOfMeasure();
    this.seasonSharedDataService.setIsSaveBtnVisibleDataSubjectData(false);
  }

  loadUnitsOfMeasure(): void {
    this.costService.getUnitsOfMeasure().subscribe(uoms => {
      this.unitsOfMeasure = uoms;
    });
  }

  loadSeasonFields(): void {
    this.isLoading = true;
    this.seasonManagerService.getSeasonById(this.seasonId).subscribe(farms => {
      this.allFieldDetailIds = [];
      (farms || []).forEach(farm => {
        (farm.fields || []).forEach(field => {
          const detailId = field.detailsId || field.id;
          if (detailId) this.allFieldDetailIds.push(detailId);
        });
      });
      this.loadInputOptions();
    }, () => { this.isLoading = false; });
  }

  loadInputOptions(): void {
    const uniqueInputs = new Map<string, any>();
    let completed = 0;

    if (this.allFieldDetailIds.length === 0) {
      this.isLoading = false;
      return;
    }

    this.allFieldDetailIds.forEach(detailId => {
      this.costService.getInputOptions(detailId).subscribe(options => {
        (options || []).forEach(opt => {
          const key = opt.inputName + '|' + opt.sourceType;
          if (!uniqueInputs.has(key)) {
            uniqueInputs.set(key, { type: opt.inputName, sourceType: opt.sourceType, costValue: 0, unitOfMeasureId: null });
          }
        });
        completed++;
        if (completed === this.allFieldDetailIds.length) {
          this.costInputs = [...uniqueInputs.values()];
          this.isLoading = false;
        }
      }, () => {
        completed++;
        if (completed === this.allFieldDetailIds.length) {
          this.costInputs = [...uniqueInputs.values()];
          this.isLoading = false;
        }
      });
    });
  }

  apply(): void {
    const requests = [];

    this.allFieldDetailIds.forEach(detailId => {
      if (this.fixedCostValue > 0) {
        requests.push(this.costService.upsertCost({
          seasonFieldDetailId: detailId,
          mapType: 'FIXED',
          mapCategory: 'FIXED',
          costValue: this.fixedCostValue,
          unitOfMeasureId: null
        }));
      }

      this.costInputs.forEach(input => {
        if (input.costValue > 0 && input.unitOfMeasureId) {
          requests.push(this.costService.upsertCost({
            seasonFieldDetailId: detailId,
            mapType: input.type,
            mapCategory: input.sourceType,
            costValue: input.costValue,
            unitOfMeasureId: input.unitOfMeasureId
          }));
        }
      });
    });

    if (requests.length === 0) {
      this.snackBar.open('No cost values to apply.', 'Dismiss', { duration: 3000 });
      return;
    }

    this.nav.toggleLoading(true);
    forkJoin(requests).subscribe(() => {
      this.nav.toggleLoading(false);
      this.snackBar.open('Costs applied to ' + this.allFieldDetailIds.length + ' fields.', 'Dismiss', { duration: 5000 });
    }, () => {
      this.nav.toggleLoading(false);
      this.snackBar.open('Error applying costs.', 'Dismiss', { duration: 5000 });
    });
  }
}
